import React, { useEffect, useState } from 'react';
import { Col, Grid, Row } from 'react-flexbox-grid';
import { Link, withRouter } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import 'react-bootstrap-table/dist/react-bootstrap-table.min.css';
import ShowPreEExceptionDialog from '../PreEExceptionDialog';
import { Breadcrumb, Table } from 'antd';
import FormHeader from '../../../components/FormHeader';
import ShowPreEFormExceptionDeleteDialog from '../PreEFormExceptionDeleteDialog';
import { projectStatusCodeMap } from '../../../components/ProjectStatusCodeMap';
import {
  isCreatorOrAdministratorOrInternalContributor,
  isViewOnlyOrExternalView,
  isExternalContributor,
  isAdministrator,
} from '../../../utils/UserInfo';
import {
  getExceptionAttachments,
  getPreEFormExceptions,
  missedApproverRoles,
} from '../../../api/PreEForm';
import { confirmAlert } from 'react-confirm-alert';
import Button from '../../../components/Button';
import Errors from '../../../components/Errors';
import { getTeamMembers } from '../../../api';
import moment from 'moment';
import {
  SearchAndDropdownComponent,
  commonSorter,
  composeFilters,
  searchAndDropDownFilters,
  searchAndDropDownOnFilters,
  commonFilter,
} from 'utils/AntdTableFunctions';
import EmptyView from 'components/EmptyView';

function PreEFormExceptionGrid(props) {
  const [error, setError] = useState(null);
  const [preEFormExceptions, setPreEFormExceptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const formId = props.match.params.formId;

  useEffect(() => {
    // ✅ When the parent has already fetched exceptions and passed them down
    //    (e.g. immediately after a save via updateExceptionsState), sync that
    //    data directly into local state — no extra API round-trip needed and
    //    the global spinner was already controlled by updateExceptionsState.
    if (props.preEFormExceptions !== null && props.preEFormExceptions !== undefined) {
      setPreEFormExceptions(props.preEFormExceptions);
      setLoading(false);
      return;
    }

    // ✅ Initial load or direct navigation: parent hasn't fetched yet (prop is
    //    null), so the grid fetches its own data and manages its own loading state.
    let cancelled = false;
    setLoading(true);

    const getData = async () => {
      try {
        const data = await getPreEFormExceptions(formId);
        if (!cancelled) setPreEFormExceptions(data);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    getData();

    // ✅ Cleanup prevents stale setState on unmounted component
    return () => { cancelled = true; };

  // ✅ Depends on the prop value itself — re-runs whenever the parent sets
  //    new exceptions data after a save, keeping the grid in sync without
  //    an extra fetch. Also re-runs on formId change for direct navigation.
  }, [formId, props.preEFormExceptions]);

  const EditCellFormatter = (cell, row) => {
    return (
      <Link to={`#`} onClick={() => editForm(cell, row)}>
        <strong> {row.title} </strong>
      </Link>
    );
  };

  const ApprovalCellFormatter = (cell, row) => {
    const { status, project } = props;
    return (
      <Link to={`#`} onClick={() => approvals(cell, row)}>
        <strong> Approvals </strong>
      </Link>
    );
  };

  const { status, project } = props;

  const ClosedStatusFormatter = (cell, row) => {
    return row.closed === 'Y' ? 'Closed' : 'Open';
  };

  const ResolutionDateFormatter = (cell, row) => {
    return moment(row.resolutionDate).format('MM/DD/YYYY');
  };

  async function editForm(cell, row) {
    const { updateExceptionsState } = props;
    let attachments = [];
    let teams = [];
    const formId = props.match.params.formId;
    const exceptionId =
      row === null ? null : row.preEnergizationFormExceptionId;
    if (exceptionId) {
      attachments = await getExceptionAttachments(exceptionId);
    }
    teams = await getTeamMembers(props.project.id);

    const propsData = {
      formId: formId,
      exceptionId: exceptionId,
      updateState: () => updateExceptionsState(formId),
      viewOnly:
        props.project.projectStatus === projectStatusCodeMap['CLOSED'] ||
        isViewOnlyOrExternalView(),
      isCreatorAdminIntCont: isCreatorOrAdministratorOrInternalContributor(),
      deleteException: () => deleteException(cell, row),
      isExternalContributor: isExternalContributor(),
      teams: teams,
      projectId: props.project.id,
      attachments: attachments,
      updateFormState: props.updateFormState,
      energized: props.preEForm.energized,
      exceptionViewMode: row == null && cell == null ? 'CREATE' : 'VIEW',
    };
    ShowPreEExceptionDialog(propsData);
  }

  const deleteException = (cell, row) => {
    const { updateExceptionsState } = props;
    const formId = props.match.params.formId;
    if (
      row.status &&
      (row.status === 'Pending' ||
        row.status === 'Cancelled' ||
        (isAdministrator() &&
          row.status === 'Approved' &&
          props.preEForm.energized !== 'Y'))
    ) {
      ShowPreEFormExceptionDeleteDialog({
        preEnergizationFormException: row,
        updateState: () => updateExceptionsState(formId),
        updateFormState: () => props.updateFormState(),
      });
    } else {
      inValidDeleteConfirmation(row.status);
    }
  };

  function handleErrors(error) {
    setError(error);
    window.scrollTo(0, 0);
  }

  function closeErrorBox() {
    setError(null);
  }

  async function approvals(cell, row) {
    const { updateExceptionsState, status, project } = props;
    const formId = props.match.params.formId;
    const exceptionId =
      row === null ? null : row.preEnergizationFormExceptionId;

    let missingRoles = await missedApproverRoles(exceptionId);
    if (missingRoles && missingRoles.length > 0) {
      let roles = missingRoles.map((t) => t.roleName).join(', ');
      missingRolesConfirmation(roles);
    } else {
      props.history.push(
        `/${status}/${project.id}/peform/${formId}/exceptions/${row.preEnergizationFormExceptionId}/approvals`
      );
    }
  }

  const missingRolesConfirmation = (missingRoles) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className='react-confirm-alert-body'>
            <p> Below approver roles are missing, Please add.</p>
            <br />
            <p>{missingRoles}</p>
            <br />
            <div className='btn ok-2'>
              <Button
                className='btn btn-primary start-5'
                onClickHandler={onClose}
              >
                {' '}Ok{' '}
              </Button>
            </div>
          </div>
        );
      },
    });
  };

  const inValidDeleteConfirmation = (status) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className='react-confirm-alert-body'>
            <p>
              {' '}
              This exception form is in {status} status. Cannot be deleted.
            </p>
            <br />
            <br />
            <div className='btn ok-2'>
              <Button
                className='btn btn-primary start-5'
                onClickHandler={onClose}
              >
                {' '}Ok{' '}
              </Button>
            </div>
          </div>
        );
      },
    });
  };

  const columns = [
    {
      dataIndex: 'title',
      key: 'title',
      title: 'Title',
      defaultSortOrder: 'ascend',
      render: EditCellFormatter,
      filters: searchAndDropDownFilters('title', preEFormExceptions),
      ...SearchAndDropdownComponent(),
      onFilter: (value, record) =>
        searchAndDropDownOnFilters(value, record, 'title'),
      sorter: (a, b) => commonSorter(a, b, 'title'),
    },
    {
      dataIndex: 'primaryContactFullName',
      key: 'primaryContactFullName',
      title: 'Primary Contact',
      filters: searchAndDropDownFilters(
        'primaryContactFullName',
        preEFormExceptions
      ),
      ...SearchAndDropdownComponent(),
      onFilter: (value, record) =>
        searchAndDropDownOnFilters(value, record, 'primaryContactFullName'),
      sorter: (a, b) => commonSorter(a, b, 'primaryContactFullName'),
    },
    {
      dataIndex: 'preEnergizationFormExceptionId',
      key: 'preEnergizationFormExceptionId',
      title:
        props.project.projectStatus !== projectStatusCodeMap['CLOSED'] &&
        isCreatorOrAdministratorOrInternalContributor()
          ? 'Approvals'
          : 'View Approvals',
      render: ApprovalCellFormatter,
    },
    {
      dataIndex: 'status',
      key: 'status',
      title: 'Approval Status',
      filters: composeFilters('status', preEFormExceptions),
      onFilter: (value, record) => commonFilter(value, record, 'status'),
      sorter: (a, b) => commonSorter(a, b, 'status'),
    },
    {
      dataIndex: 'resolutionDate',
      key: 'resolutionDate',
      title: 'Expected Resolution Date',
      render: ResolutionDateFormatter,
      filters: composeFilters(
        'resolutionDate',
        preEFormExceptions,
        'PreEFormExceptionGrid'
      ),
      onFilter: (value, record) =>
        commonFilter(value, record, 'resolutionDate', 'PreEFormExceptionGrid'),
      sorter: (a, b) => commonSorter(a, b, 'resolutionDate'),
    },
    {
      dataIndex: 'closed',
      key: 'closed',
      title: 'Status',
      render: ClosedStatusFormatter,
    },
  ];

  const NoDataIndication = () =>
    loading ? <Spinner animation='border' variant='primary' /> : <EmptyView />;

  return (
    <>
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link
            to={`/${status}/${props.project.id}/peform`}
            onClick={() => props.updateFormState()}
            style={{ fontsize: '30px' }}
          >
            Pre-energization Form List
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>{props.project.name}</Breadcrumb.Item>
      </Breadcrumb>
      <FormHeader
        title={'Pre-Energization Form Exceptions'}
        onCreateForm={() => {
          editForm(null, null);
        }}
        buttonName={'Create Exception'}
        renderCreate={
          project.projectStatus !== projectStatusCodeMap['CLOSED'] &&
          !isViewOnlyOrExternalView() &&
          props.preEForm &&
          props.preEForm.energized !== 'Y'
        }
      />

      <Grid fluid>
        <Row />
        <Row>
          <Errors error={error} onCloseBox={closeErrorBox} />
        </Row>
        <Row>
          <Col lg={12}>
            <div className='responsive-scroll antd-table-custom-css'>
              <Table
                columns={columns}
                dataSource={preEFormExceptions}
                pagination={false}
                locale={{
                  emptyText: <NoDataIndication />,
                }}
              />
            </div>
          </Col>
        </Row>
      </Grid>
    </>
  );
}

export default withRouter(PreEFormExceptionGrid);
